export interface OptimizeDryNodeSummary {
  nodeId: string;
  label: string;
  status: 'evaluated' | 'skipped';
  skipReason?: string;
  steps: number;
  linkedByLayer: Record<string, number>;
  improved: number;
  carried: number;
  pending: number;
  shadowCandidates: number;
  shadowCandidateFailures: number;
  gaps: Record<string, number>;
  rowBridge: 'available' | 'unavailable';
}

export interface OptimizeDrySummary {
  ok: true;
  runId: string;
  workflowId: string;
  workspace: string;
  rowBridge: {
    status: 'full' | 'degraded';
    reason?: string;
  };
  totals: {
    nodes: number;
    steps: number;
    linkedSteps: number;
    verifiedSteps: number;
    improved: number;
    carried: number;
    pending: number;
    shadowCandidates: number;
    shadowCandidateFailures: number;
    gaps: number;
    skippedNodes: number;
    skippedSteps: number;
    linkedPercent: number;
    verifiedPercent: number;
  };
  nodes: OptimizeDryNodeSummary[];
}

const percent = (part: number, total: number): number => {
  if (total <= 0) return 0;
  return Number(((part / total) * 100).toFixed(1));
};

const formatLayerCounts = (counts: Record<string, number>): string => {
  const entries = Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
  return entries.length === 0
    ? '-'
    : entries.map(([layer, count]) => `L${layer}:${count}`).join(',');
};

const formatGapCounts = (counts: Record<string, number>): string => {
  const entries = Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
  return entries.length === 0
    ? '-'
    : entries.map(([reason, count]) => `${reason}:${count}`).join(',');
};

export const formatOptimizeDrySummary = (summary: OptimizeDrySummary): string => {
  const lines: string[] = [];
  lines.push(`Optimize dry-run for workflow ${summary.workflowId} run ${summary.runId}`);
  lines.push(`Workspace: ${summary.workspace}`);
  lines.push(
    `Rows: ${summary.rowBridge.status}${summary.rowBridge.reason ? ` (${summary.rowBridge.reason})` : ''}`
  );
  lines.push('');
  lines.push(
    [
      'node'.padEnd(10),
      'status'.padEnd(14),
      'label'.padEnd(24),
      'steps'.padStart(5),
      'linked'.padEnd(14),
      'impr'.padStart(4),
      'carry'.padStart(5),
      'pend'.padStart(4),
      'shad'.padStart(4),
      'shfail'.padStart(6),
      'gaps',
    ].join('  ')
  );
  for (const node of summary.nodes) {
    const label = node.label.length > 24 ? `${node.label.slice(0, 21)}...` : node.label;
    const status =
      node.status === 'skipped' ? `skipped:${node.skipReason ?? 'unknown'}` : 'evaluated';
    lines.push(
      [
        node.nodeId.slice(0, 8).padEnd(10),
        status.length > 14 ? `${status.slice(0, 11)}...` : status.padEnd(14),
        label.padEnd(24),
        String(node.steps).padStart(5),
        formatLayerCounts(node.linkedByLayer).padEnd(14),
        String(node.improved).padStart(4),
        String(node.carried).padStart(5),
        String(node.pending).padStart(4),
        String(node.shadowCandidates).padStart(4),
        String(node.shadowCandidateFailures).padStart(6),
        formatGapCounts(node.gaps),
      ].join('  ')
    );
  }
  lines.push('');
  lines.push(
    `Totals: ${summary.totals.nodes} node(s), ${summary.totals.steps} step(s), ` +
      `${summary.totals.linkedPercent}% linked, ${summary.totals.verifiedPercent}% verified, ` +
      `${summary.totals.pending} pending, ${summary.totals.gaps} gap(s), ` +
      `${summary.totals.shadowCandidates} shadow candidate(s), ` +
      `${summary.totals.shadowCandidateFailures} shadow failure(s), ` +
      `${summary.totals.skippedNodes} skipped node(s) / ${summary.totals.skippedSteps} skipped step(s)`
  );
  if (
    summary.totals.steps > 0 &&
    summary.totals.improved === 0 &&
    summary.totals.linkedSteps === 0 &&
    summary.totals.pending === 0 &&
    summary.totals.gaps === 0
  ) {
    lines.push('Nothing to optimize: evaluated steps were carried through or skipped cleanly.');
  }
  return lines.join('\n');
};

export const buildOptimizeDryTotals = (
  nodes: OptimizeDryNodeSummary[]
): OptimizeDrySummary['totals'] => {
  const totals = {
    nodes: 0,
    steps: 0,
    linkedSteps: 0,
    verifiedSteps: 0,
    improved: 0,
    carried: 0,
    pending: 0,
    shadowCandidates: 0,
    shadowCandidateFailures: 0,
    gaps: 0,
    skippedNodes: 0,
    skippedSteps: 0,
    linkedPercent: 0,
    verifiedPercent: 0,
  };
  for (const node of nodes) {
    if (node.status === 'skipped') {
      totals.skippedNodes += 1;
      totals.skippedSteps += node.steps;
      continue;
    }
    totals.nodes += 1;
    totals.steps += node.steps;
    totals.linkedSteps += Object.values(node.linkedByLayer).reduce((sum, count) => sum + count, 0);
    totals.verifiedSteps += node.improved + node.carried;
    totals.improved += node.improved;
    totals.carried += node.carried;
    totals.pending += node.pending;
    totals.shadowCandidates += node.shadowCandidates;
    totals.shadowCandidateFailures += node.shadowCandidateFailures;
    totals.gaps += Object.values(node.gaps).reduce((sum, count) => sum + count, 0);
  }
  totals.linkedPercent = percent(totals.linkedSteps, totals.steps);
  totals.verifiedPercent = percent(totals.verifiedSteps, totals.steps);
  return totals;
};
